import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState, useEffect } from "react";
import { apiClient } from "@/services/api";
import { toast } from "sonner";

interface ClientProject {
  id: string;
  name: string;
  status?: string;
}

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function InviteUserModal({ isOpen, onClose, onSuccess }: InviteUserModalProps) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [role, setRole] = useState("");
  const [clientProjectId, setClientProjectId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [clientProjects, setClientProjects] = useState<ClientProject[]>([]);

  // Fetch client projects from API
  useEffect(() => {
    const fetchClientProjects = async () => {
      try {
        const response = await apiClient.get('/clients?status=active');
        if (response.data?.success && response.data?.data?.clients) {
          setClientProjects(response.data.data.clients);
        }
      } catch (error) {
        console.error('Error fetching client projects:', error);
        toast.error('Error loading client projects');
      }
    };

    if (isOpen) {
      fetchClientProjects();
    }
  }, [isOpen]);


  const handleInvite = async () => {
    setIsLoading(true);
    setError("");

    // Basic validation
    if (!email || !role) {
      setError("Email y rol son requeridos.");
      setIsLoading(false);
      return;
    }
    
    // Non-admin users require a client project
    if (role !== "admin" && !clientProjectId) {
      setError("Debe seleccionar un proyecto cliente para usuarios no administradores.");
      setIsLoading(false);
      return;
    }

    try {
      const requestData = {
        email,
        role,
        fullName: fullName || undefined,
        avatarUrl: avatarUrl || undefined,
        ...(role !== 'admin' && { clientProjectId })
      };

      const response = await apiClient.post('/users/invite', requestData);

      if (response.data?.success) {
        toast.success('Usuario invitado exitosamente');
        // Reset form
        setEmail("");
        setFullName("");
        setAvatarUrl("");
        setRole("");
        setClientProjectId("");
        onSuccess?.(); // Notify parent of successful invitation
        onClose();
      } else {
        throw new Error(response.data?.error?.message || 'Error al invitar usuario');
      }
    } catch (error: any) {
      console.error("Error inviting user:", error);
      const errorMessage = error.response?.data?.error?.message || error.message || "Error al invitar usuario";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Invitar Nuevo Usuario</DialogTitle>
          <DialogDescription>
            Ingresa los detalles del usuario que deseas invitar. Recibirán un correo electrónico para configurar su cuenta.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="email" className="text-right">
              Email
            </Label>
            <Input 
              id="email" 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              className="col-span-3" 
              required
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="fullName" className="text-right">
              Nombre Completo
            </Label>
            <Input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="col-span-3"
              placeholder="Opcional"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="avatarUrl" className="text-right">
              Foto de Perfil
            </Label>
            <Input
              id="avatarUrl"
              type="url"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              className="col-span-3"
              placeholder="URL de la imagen (opcional)"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="role" className="text-right">
              Rol
            </Label>
            <Select onValueChange={setRole} value={role}>
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Selecciona un rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrador</SelectItem>
                <SelectItem value="developer">Desarrollador</SelectItem>
                <SelectItem value="viewer">Visualizador</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {role && role !== "admin" && (
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="client-project" className="text-right">
                Proyecto Cliente
              </Label>
              <Select onValueChange={setClientProjectId} value={clientProjectId}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Selecciona un proyecto" />
                </SelectTrigger>
                <SelectContent>
                  {clientProjects.length === 0 ? (
                    <SelectItem value="" disabled>Cargando proyectos...</SelectItem>
                  ) : (
                    clientProjects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}
          {error && <p className="text-red-500 text-sm col-span-4 text-center">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>Cancelar</Button>
          <Button onClick={handleInvite} disabled={isLoading || !email || !role || (role !== 'admin' && !clientProjectId)}>
            {isLoading ? "Enviando..." : "Enviar Invitación"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
